import { useEnrichCcdaDocuments } from '@/hooks/useEnrichCcdaDocuments';
import { useTRPC } from '@/utils/trpc';
import { resolveId } from '@medplum/core';
import { formatHumanNameLastFirst } from '@liza/fhir-utils';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { EncounterDetail } from '@liza/ui/components/records';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  id: string;
  medplumPatientId: string;
};

const formatPractitionerName = (firstName: string, lastName: string): string =>
  formatHumanNameLastFirst({ family: lastName || undefined, given: firstName ? [firstName] : undefined });

export const Encounter = ({ id, medplumPatientId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();

  const encounterQuery = useQuery(trpc.patient.encounter.get.queryOptions({ id }));

  const documentsQuery = useQuery(
    trpc.patient.documentReference.list.queryOptions({
      medplumPatientId,
      options: { searchParams: { encounter: `Encounter/${id}` } },
    }),
  );

  const documents = useEnrichCcdaDocuments(documentsQuery.data?.documentReferences);

  const practitionerRefs = useMemo(() => {
    const refs = encounterQuery.data?.encounter.participant
      ?.map((p) => p.individual?.reference)
      .filter((ref): ref is string => !!ref?.startsWith('Practitioner/'));
    return refs ?? [];
  }, [encounterQuery.data?.encounter.participant]);

  const practitionerQueries = useQueries({
    queries: practitionerRefs.map((ref) => {
      const practitionerId = resolveId({ reference: ref }) ?? '';
      return {
        ...trpc.practitioner.get.queryOptions({ practitionerId }),
        enabled: !!practitionerId,
      };
    }),
  });

  const participantDisplays = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    practitionerRefs.forEach((ref, i) => {
      const data = practitionerQueries[i]?.data;
      if (data) map[ref] = formatPractitionerName(data.firstName, data.lastName);
    });
    return map;
  }, [practitionerRefs, practitionerQueries]);

  return (
    <QueryStateHandler query={encounterQuery} emptyMessage={t('noEncountersFound')}>
      {(data) => (
        <EncounterDetail
          encounter={data.encounter}
          participantDisplays={participantDisplays}
          // Documents are progressive enhancement; query errors are intentionally swallowed.
          documents={documents}
          isLoadingDocuments={documentsQuery.isLoading}
        />
      )}
    </QueryStateHandler>
  );
};
